"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ArrowLeft, Check, Loader2, Sparkles } from "lucide-react";
import {
  useGetPricingQuery,
  useLazyGetCheckoutQuery,
  useGetSubscriptionDetailsQuery,
} from "@/services/paymentApi";
import TransactionHistoryCard from "./TransactionHistoryCard";
import CurrentPlanCard from "./CurrentPlanCard";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { useGetBillingPaymentPricingQuery } from "@/services/chatApi";
import ReactMarkdown from "react-markdown";
import { darkMarkdownComponents } from "@/components/shared/markdownUtils";

type PricingPlan = {
  name: string;
  price_id: string;
  description: string;
  price_monthly: number;
  currency: string;
  has_annual_toggle: boolean;
  features_header: string | null;
  features: string[];
};

export default function PricingPageContent() {
  const user = useSelector((s: RootState) => s.auth.user);
  const { data, isLoading } = useGetPricingQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: subResp, refetch: refetchSub } = useGetSubscriptionDetailsQuery(user?.uid);
  const currentPlan = subResp?.subscription?.type || "Free Tier";
  const [triggerCheckout] = useLazyGetCheckoutQuery();
  const [transactionRefreshSignal, setTransactionRefreshSignal] = useState(0);
  const [loadingPriceId, setLoadingPriceId] = useState<
    string | null
  >(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  const { data: billingPolicyData, isLoading: isLoadingPolicy } = useGetBillingPaymentPricingQuery(undefined, { skip: typeof window === 'undefined' });

  useEffect(() => {
    const handlePaddleEvent = async (e: any) => {
      if (e.detail?.name === "checkout.completed") {
        setIsProcessingPayment(true);
        setTimeout(async () => {
          try {
            await refetchSub();
            setTransactionRefreshSignal((current) => current + 1);
          } catch (error) {
            console.error("Failed to refresh subscription details", error);
          } finally {
            setIsProcessingPayment(false);
          }
        }, 3000);
      }
    };
    window.addEventListener("orignly-paddle-event", handlePaddleEvent);
    return () => window.removeEventListener("orignly-paddle-event", handlePaddleEvent);
  }, [refetchSub]);

  const title = data?.title || "Pricing";
  const subtitle =
    data?.subtitle || "Start free, unlock advanced intelligence";
  const selectedAudience = data?.selected_audience || "Individual";
  const plans: PricingPlan[] = Array.isArray(data?.plans) && data.plans.length > 0
    ? data.plans
    : Array.isArray(billingPolicyData?.plans)
      ? billingPolicyData.plans
      : [];

  const handleBack = () => {
    if (typeof window !== "undefined") {
      const isInternal = document.referrer.includes(window.location.host);
      if (isInternal && window.history.length > 1) {
        router.back();
      } else {
        router.push("/");
      }
    }
  };

  const handleCheckout = async (priceId: string) => {
    if (!user) {
      router.push("/");
      return;
    }

    try {
      setLoadingPriceId(priceId);
      const response = await triggerCheckout(priceId).unwrap();
      const checkoutUrl =
        response?.checkout_url || response?.data?.checkout_url;

      if (checkoutUrl && typeof window !== "undefined") {
        const rewrittenCheckoutUrl = (() => {
          try {
            const parsedUrl = new URL(checkoutUrl);
            const paddleTransactionToken =
              parsedUrl.searchParams.get("_ptxn");

            if (paddleTransactionToken) {
              if (window.Paddle?.Checkout?.open) {
                window.Paddle.Checkout.open({
                  transactionId: paddleTransactionToken,
                });
                return {
                  isInternal: true,
                  url: "",
                  handled: true,
                };
              }

              const targetPath = pathname || "/pricing";
              return {
                isInternal: true,
                url: `${targetPath}?_ptxn=${encodeURIComponent(
                  paddleTransactionToken
                )}`,
                handled: false,
              };
            }

            if (
              window.location.hostname === "localhost" &&
              parsedUrl.hostname === "localhost"
            ) {
              return {
                isInternal: false,
                url: `http://localhost:3000${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`,
                handled: false,
              };
            }

            return {
              isInternal: false,
              url: parsedUrl.toString(),
              handled: false,
            };
          } catch {
            return {
              isInternal: false,
              url: checkoutUrl,
              handled: false,
            };
          }
        })();

        if (rewrittenCheckoutUrl.handled) {
          return;
        }

        if (rewrittenCheckoutUrl.isInternal) {
          window.location.href = rewrittenCheckoutUrl.url;
        } else {
          window.location.replace(rewrittenCheckoutUrl.url);
        }
      }
    } catch (error) {
      console.error("Failed to start checkout:", error);
    } finally {
      setLoadingPriceId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(235,99,139,0.10),transparent_24%),linear-gradient(180deg,#050505_0%,#080808_45%,#050505_100%)] text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(235,99,139,0.08),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(172,39,79,0.06),transparent_22%)]" />

      <button
        onClick={handleBack}
        className="fixed left-4 top-2 md:left-8 md:top-6 z-50 inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-200 transition hover:bg-white/10 hover:text-white"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Go back
      </button>

      <main className={user ? "relative mx-auto flex max-w-7xl flex-col px-5 pt-6 pb-2 md:px-8 md:pt-6 md:pb-3 lg:pt-6 lg:pb-4" : "relative mx-auto flex max-w-7xl flex-col px-5 py-2 md:px-8 md:py-3 lg:py-4"}>
        {user && (
          <>
            <div className="flex flex-col gap-2 lg:grid lg:grid-cols-2 lg:items-stretch">
              <div className="lg:col-span-1 w-full h-full">
                <CurrentPlanCard
                  onRefetchNeeded={() =>
                    setTransactionRefreshSignal((current) => current + 1)
                  }
                  isExternalLoading={isProcessingPayment}
                />
              </div>
              <div className="lg:col-span-1 w-full h-full">
                <TransactionHistoryCard
                  refreshSignal={transactionRefreshSignal}
                  isExternalLoading={isProcessingPayment}
                />
              </div>
            </div>
          </>
        )}

        <section className="mt-2 flex flex-col rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.015))] p-3 shadow-[0_24px_100px_rgba(0,0,0,0.38)] backdrop-blur-sm md:p-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-[1.5rem] font-semibold tracking-tight text-white md:text-[2.0rem]">
                {title}
              </h1>
            </div>

            {/* <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-2.5 text-[13px] text-zinc-300 md:text-sm">
              Flexible plans for solo builders and serious creators, with credits included in every tier.
            </div> */}
          </div>

          {(isLoading || isLoadingPolicy) && plans.length === 0 ? (
            <div className="mt-6 grid flex-1 gap-4 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="min-h-[320px] animate-pulse rounded-[26px] border border-white/10 bg-white/5"
                />
              ))}
            </div>
          ) : (
            <div className="mt-3 grid gap-3 lg:grid-cols-3">
              {plans.map((plan, index) => {
                const isCurrentPlan = plan.name === currentPlan;

                return (
                  <div
                    key={plan.price_id}
                    className={`flex min-h-0 flex-col rounded-[24px] border p-3 shadow-[0_20px_70px_rgba(0,0,0,0.28)] ${isCurrentPlan
                      ? "border-[#8d4b61] bg-[linear-gradient(180deg,rgba(172,39,79,0.22),rgba(255,255,255,0.04))]"
                      : "border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))]"
                      }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-[1.4rem] font-semibold leading-none text-white">
                          {plan.name}
                        </h2>
                        <p className="mt-1 text-[12px] leading-4 text-zinc-400 md:text-[13px]">
                          {plan.description}
                        </p>
                      </div>

                      {isCurrentPlan && (
                        <span className="shrink-0 whitespace-nowrap rounded-full border border-[#8d4b61] bg-[rgba(172,39,79,0.16)] px-3 py-1 text-[11px] font-medium text-[#f3b1c4]">
                          Current Plan
                        </span>
                      )}
                    </div>

                    <div className="mt-3 flex items-end gap-2">
                      <span className="text-[2.2rem] font-semibold leading-none text-white">
                        {plan.currency}
                        {plan.price_monthly}
                      </span>
                      <span className="pb-1 text-[12px] text-zinc-400 md:text-[13px]">
                        / month
                      </span>
                    </div>

                    {!user || plan.name.toLowerCase().includes("free") || plan.price_monthly === 0 ? (
                      <div className="mt-3 inline-flex w-full px-4 py-2 text-sm border border-transparent" aria-hidden="true">
                        &nbsp;
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleCheckout(plan.price_id)}
                        disabled={loadingPriceId === plan.price_id}
                        className={`mt-3 inline-flex w-full cursor-pointer items-center justify-center rounded-2xl px-4 py-2 text-sm font-medium transition hover:scale-[1.01] bg-gradient-to-r from-[#ac274f] to-[#eb638b] text-white shadow-[0_18px_40px_rgba(172,39,79,0.32)] disabled:cursor-not-allowed disabled:opacity-70`}
                      >
                        {loadingPriceId === plan.price_id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Redirecting...
                          </>
                        ) : plan.name === currentPlan ? (
                          <>Renew Plan</>
                        ) : (
                          <>Choose {plan.name}</>
                        )}
                      </button>
                    )}

                    <div className="mt-3 flex-1 rounded-2xl border border-white/8 bg-black/20 p-2.5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                        Included
                      </p>

                      {plan.features_header && (
                        <p className="mt-1 text-[12px] text-zinc-300 md:text-[13px]">
                          {plan.features_header}
                        </p>
                      )}

                      <div className="mt-2 space-y-1.5">
                        {plan.features.map((feature) => (
                          <div
                            key={feature}
                            className="flex items-start gap-2 text-[12px] text-zinc-200 md:text-[13px]"
                          >
                            <span className="mt-0.5 rounded-full bg-[rgba(172,39,79,0.16)] p-1 text-[#eb638b]">
                              <Check className="h-3 w-3" />
                            </span>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* 🔹 Billing, Payments, and Pricing Policy Section */}
        <section className="mt-6 flex flex-col rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.015))] p-6 shadow-[0_24px_100px_rgba(0,0,0,0.38)] backdrop-blur-sm md:p-8 mb-10">
          {isLoadingPolicy ? (
            <div className="flex justify-center items-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-[#eb638b]" />
            </div>
          ) : billingPolicyData?.content ? (
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown
                components={darkMarkdownComponents}
              >
                {billingPolicyData.content}
              </ReactMarkdown>
            </div>
          ) : null}
        </section>
      </main>
    </div>
  );
}
